import React, { useState, useEffect } from "react";
import axios from "axios";
import { Handshake, Send, AlertCircle, Clock, CheckCircle2, XCircle, Briefcase, Gift, Sparkles, EyeOff } from "lucide-react";
import { useNavigate, useSearchParams } from "react-router-dom";
import ContactRequestModal from "../components/ContactRequestModal";

const API = process.env.REACT_APP_BACKEND_URL;

export default function Prospects() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = searchParams.get("tab") === "recommendations"
    ? "recommendations"
    : searchParams.get("tab") === "sent" ? "sent" : "received";
  const [tab, setTab] = useState(initialTab);
  const [received, setReceived] = useState([]);
  const [sent, setSent] = useState([]);
  const [recommendations, setRecommendations] = useState([]);
  const [quota, setQuota] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(null);
  const [contactTarget, setContactTarget] = useState(null);
  const navigate = useNavigate();

  const setTabAndUrl = (next) => {
    setTab(next);
    setSearchParams(next === "received" ? {} : { tab: next });
  };

  const loadData = async () => {
    setLoading(true);
    try {
      const [receivedRes, sentRes, quotaRes, recoRes] = await Promise.all([
        axios.get(`${API}/api/contact-requests/received`, { withCredentials: true }),
        axios.get(`${API}/api/contact-requests/sent`, { withCredentials: true }),
        axios.get(`${API}/api/contact-requests/quota`, { withCredentials: true }),
        axios.get(`${API}/api/introductions/mine`, { withCredentials: true }),
      ]);
      setReceived(receivedRes.data.items || []);
      setSent(sentRes.data.items || []);
      setQuota(quotaRes.data);
      setRecommendations(recoRes.data.items || []);
    } catch (err) {
      console.error("Error loading data:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const handleAccept = async (requestId) => {
    console.log("🟢 CLICK ACCEPT - Request ID:", requestId);
    if (!window.confirm("Accepter cette demande de contact ?")) return;
    setProcessing(requestId);
    try {
      console.log("📤 Sending POST to:", `${API}/api/contact-requests/${requestId}/accept`);
      const response = await axios.post(`${API}/api/contact-requests/${requestId}/accept`, {}, { withCredentials: true });
      console.log("✅ Response:", response.data);
      await loadData(); // Reload data after success
      alert("Demande acceptée avec succès !");
    } catch (err) {
      console.error("❌ Error accepting:", err);
      console.error("❌ Error response:", err.response?.data);
      alert(err.response?.data?.detail || "Erreur lors de l'acceptation");
    } finally {
      setProcessing(null);
    }
  };

  const handleRefuse = async (requestId) => {
    console.log("🔴 CLICK REFUSE - Request ID:", requestId);
    if (!window.confirm("Refuser cette demande ? Cette action est définitive.")) return;
    setProcessing(requestId);
    try {
      console.log("📤 Sending POST to:", `${API}/api/contact-requests/${requestId}/refuse`);
      const response = await axios.post(`${API}/api/contact-requests/${requestId}/refuse`, {}, { withCredentials: true });
      console.log("✅ Response:", response.data);
      await loadData(); // Reload data after success
      alert("Demande refusée");
    } catch (err) {
      console.error("❌ Error refusing:", err);
      console.error("❌ Error response:", err.response?.data);
      alert(err.response?.data?.detail || "Erreur lors du refus");
    } finally {
      setProcessing(null);
    }
  };

  const handleIgnoreReco = async (introId) => {
    if (!window.confirm("Ignorer cette recommandation ?")) return;
    setProcessing(introId);
    try {
      await axios.post(`${API}/api/introductions/${introId}/ignore`, {}, { withCredentials: true });
      await loadData();
    } catch (err) {
      alert(err.response?.data?.detail || "Erreur");
    } finally {
      setProcessing(null);
    }
  };

  const handleAskContactFromReco = (other) => {
    if (!other?.profile_id) {
      alert("Profil indisponible");
      return;
    }
    setContactTarget({
      _id: other.profile_id,
      nom_prenom: other.nom_prenom,
      entreprise: other.entreprise,
    });
  };

  const getStatusBadge = (status) => {
    if (status === "pending") return <span className="cbm-badge cbm-badge-new text-xs">⏳ En attente</span>;
    if (status === "accepted") return <span className="cbm-badge text-xs" style={{ background: "rgba(34,197,94,0.1)", color: "#22c55e" }}>✅ Acceptée</span>;
    if (status === "refused") return <span className="cbm-badge cbm-badge-closed text-xs">❌ Refusée</span>;
    return null;
  };

  const getRelatedIcon = (type) => {
    if (type === "opportunity") return <Briefcase size={12} className="text-gold" />;
    if (type === "benefit") return <Gift size={12} className="text-gold" />;
    return null;
  };

  const pendingReceivedCount = received.filter((r) => r.status === "pending").length;

  if (loading) {
    return (
      <div className="cbm-main-area">
        <div className="cbm-container py-6">
          <p className="text-cbm-muted text-sm">Chargement...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="cbm-main-area">
      <div className="cbm-container py-5">
        {/* Container max-width pour meilleure lisibilité */}
        <div style={{ maxWidth: "900px", margin: "0 auto" }} className="space-y-5">
          {/* Header */}
          <div>
            <h1 className="font-outfit font-bold text-white text-2xl sm:text-3xl mb-2">Prospects</h1>
            <p className="text-cbm-secondary text-sm sm:text-base">Gérez vos demandes de mise en relation</p>
          </div>

          {/* Quota info */}
          {quota && (
            <div className="bg-navy-darker rounded-lg p-3.5 flex items-center gap-3">
              <AlertCircle size={18} className="text-gold flex-shrink-0" />
              <p className="text-cbm-secondary text-sm">
                Il vous reste <span className="text-gold font-semibold">{quota.remaining}/{quota.quota}</span> demande{quota.remaining > 1 ? "s" : ""} ce mois-ci
              </p>
            </div>
          )}

          {/* Tabs */}
          <div className="flex gap-2 border-b border-navy-light">
            <button
              onClick={() => setTabAndUrl("received")}
              className={`pb-3 px-4 font-medium text-sm transition-all relative ${
                tab === "received" ? "text-white" : "text-cbm-muted hover:text-white"
              }`}
              data-testid="tab-received"
            >
              Demandes reçues
              {pendingReceivedCount > 0 && (
                <span className="ml-2 inline-flex items-center justify-center w-5 h-5 text-xs font-semibold rounded-full bg-gold text-navy-darker">
                  {pendingReceivedCount}
                </span>
              )}
              {tab === "received" && (
                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-gold"></div>
              )}
            </button>
            <button
              onClick={() => setTabAndUrl("sent")}
              className={`pb-3 px-4 font-medium text-sm transition-all relative ${
                tab === "sent" ? "text-white" : "text-cbm-muted hover:text-white"
              }`}
              data-testid="tab-sent"
            >
              Demandes envoyées
              {tab === "sent" && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-gold"></div>}
            </button>
            <button
              onClick={() => setTabAndUrl("recommendations")}
              className={`pb-3 px-4 font-medium text-sm transition-all relative ${
                tab === "recommendations" ? "text-white" : "text-cbm-muted hover:text-white"
              }`}
              data-testid="tab-recommendations"
            >
              Recommandations
              {recommendations.length > 0 && (
                <span className="ml-2 inline-flex items-center justify-center w-5 h-5 text-xs font-semibold rounded-full bg-gold text-navy-darker">
                  {recommendations.length}
                </span>
              )}
              {tab === "recommendations" && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-gold"></div>}
            </button>
          </div>

          {/* Content */}
          {tab === "received" && (
            <div className="space-y-3">
              {received.length === 0 ? (
                <div className="cbm-card p-8 text-center">
                  <Handshake size={32} className="text-cbm-muted mx-auto mb-2" />
                  <p className="text-white text-sm font-medium">Aucune demande reçue</p>
                  <p className="text-cbm-muted text-xs mt-1">Vous recevrez ici les demandes de contact des autres membres</p>
                </div>
              ) : (
                received.map((req) => (
                  <div key={req._id} className="cbm-card p-4" data-testid={`request-${req._id}`}>
                    {/* Ligne 1: Avatar + Nom + Statut */}
                    <div className="flex items-start gap-3 mb-3">
                      <div className="w-10 h-10 rounded-full bg-navy-darker flex items-center justify-center flex-shrink-0 text-gold font-semibold text-sm">
                        {req.from_profile?.nom_prenom?.charAt(0) || "?"}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2 mb-1">
                          <button
                            onClick={() => navigate(`/annuaire/${req.from_profile?._id}`)}
                            className="text-white font-semibold text-sm hover:text-gold transition-colors"
                          >
                            {req.from_profile?.nom_prenom || "Membre"}
                          </button>
                          {getStatusBadge(req.status)}
                        </div>
                        <p className="text-cbm-secondary text-xs">
                          {req.from_profile?.fonction && `${req.from_profile.fonction}`}
                          {req.from_profile?.entreprise && ` · ${req.from_profile.entreprise}`}
                        </p>
                      </div>
                    </div>

                    {/* Ligne 2: Motif */}
                    <div className="bg-navy-darker rounded p-2.5 mb-2">
                      <p className="text-cbm-label text-xs mb-0.5">Motif</p>
                      <div className="flex items-center gap-2">
                        <p className="text-white text-sm">{req.reason}</p>
                        {req.related_type && req.related_name && (
                          <span className="flex items-center gap-1 text-gold text-xs">
                            {getRelatedIcon(req.related_type)}
                            {req.related_name}
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Ligne 3: Message */}
                    <div className="bg-navy-darker rounded p-2.5 mb-3">
                      <p className="text-cbm-label text-xs mb-0.5">Message</p>
                      <p className="text-cbm-secondary text-sm leading-relaxed">{req.message}</p>
                    </div>

                    {/* Ligne 4: Date */}
                    <p className="text-cbm-muted text-xs mb-3 flex items-center gap-1">
                      <Clock size={12} />
                      {new Date(req.created_at).toLocaleDateString("fr-FR", {
                        day: "numeric",
                        month: "long",
                        year: "numeric",
                      })}
                    </p>

                    {/* Ligne 5: Actions */}
                    {req.status === "pending" && (
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleAccept(req._id)}
                          disabled={processing === req._id}
                          className="cbm-btn-primary text-xs px-4 py-2"
                          data-testid={`accept-${req._id}`}
                        >
                          <CheckCircle2 size={12} className="mr-1" />
                          {processing === req._id ? "..." : "Accepter"}
                        </button>
                        <button
                          onClick={() => handleRefuse(req._id)}
                          disabled={processing === req._id}
                          className="text-xs px-4 py-2"
                          style={{
                            background: "rgba(239,68,68,0.1)",
                            border: "1px solid rgba(239,68,68,0.3)",
                            color: "#ef4444",
                            borderRadius: "0.5rem",
                            display: "inline-flex",
                            alignItems: "center",
                            gap: "0.25rem",
                          }}
                          data-testid={`refuse-${req._id}`}
                        >
                          <XCircle size={12} />
                          {processing === req._id ? "..." : "Refuser"}
                        </button>
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          )}

          {tab === "sent" && (
            <div className="space-y-3">
              {sent.length === 0 ? (
                <div className="cbm-card p-8 text-center">
                  <Send size={32} className="text-cbm-muted mx-auto mb-2" />
                  <p className="text-white text-sm font-medium">Aucune demande envoyée</p>
                  <p className="text-cbm-muted text-xs mt-1">Consultez l'annuaire pour découvrir des membres et leur demander le contact</p>
                </div>
              ) : (
                sent.map((req) => (
                  <div key={req._id} className="cbm-card p-4">
                    {/* Ligne 1: Avatar + Nom + Statut */}
                    <div className="flex items-start gap-3 mb-3">
                      <div className="w-10 h-10 rounded-full bg-navy-darker flex items-center justify-center flex-shrink-0 text-gold font-semibold text-sm">
                        {req.to_profile?.nom_prenom?.charAt(0) || "?"}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2 mb-1">
                          <button
                            onClick={() => navigate(`/annuaire/${req.to_profile?._id}`)}
                            className="text-white font-semibold text-sm hover:text-gold transition-colors"
                          >
                            {req.to_profile?.nom_prenom || "Membre"}
                          </button>
                          {getStatusBadge(req.status)}
                        </div>
                        <p className="text-cbm-secondary text-xs">
                          {req.to_profile?.fonction && `${req.to_profile.fonction}`}
                          {req.to_profile?.entreprise && ` · ${req.to_profile.entreprise}`}
                        </p>
                      </div>
                    </div>

                    {/* Ligne 2: Motif */}
                    <div className="bg-navy-darker rounded p-2.5 mb-2">
                      <p className="text-cbm-label text-xs mb-0.5">Motif</p>
                      <div className="flex items-center gap-2">
                        <p className="text-white text-sm">{req.reason}</p>
                        {req.related_type && req.related_name && (
                          <span className="flex items-center gap-1 text-gold text-xs">
                            {getRelatedIcon(req.related_type)}
                            {req.related_name}
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Ligne 3: Message */}
                    <div className="bg-navy-darker rounded p-2.5 mb-3">
                      <p className="text-cbm-label text-xs mb-0.5">Votre message</p>
                      <p className="text-cbm-secondary text-sm leading-relaxed">{req.message}</p>
                    </div>

                    {/* Ligne 4: Date */}
                    <p className="text-cbm-muted text-xs flex items-center gap-1">
                      <Clock size={12} />
                      {new Date(req.created_at).toLocaleDateString("fr-FR", {
                        day: "numeric",
                        month: "long",
                        year: "numeric",
                      })}
                    </p>
                  </div>
                ))
              )}
            </div>
          )}
          {tab === "recommendations" && (
            <div className="space-y-3" data-testid="recommendations-list">
              {recommendations.length === 0 ? (
                <div className="cbm-card p-8 text-center">
                  <Sparkles size={32} className="text-cbm-muted mx-auto mb-2" />
                  <p className="text-white text-sm font-medium">Aucune recommandation pour le moment</p>
                  <p className="text-cbm-muted text-xs mt-1">L'administrateur vous proposera ici des mises en relation pertinentes.</p>
                </div>
              ) : (
                recommendations.map((r) => (
                  <div key={r._id} className="cbm-card p-4" data-testid={`recommendation-${r._id}`}>
                    {/* Header */}
                    <div className="flex items-start gap-3 mb-3">
                      <div className="w-10 h-10 rounded-full bg-navy-darker flex items-center justify-center flex-shrink-0 text-gold font-semibold text-sm">
                        {r.other?.nom_prenom?.charAt(0) || "?"}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-white text-sm leading-snug">
                          <span className="text-gold font-semibold">{r.admin_name || "L'administrateur"}</span>
                          <span className="text-cbm-secondary"> vous recommande d'échanger avec </span>
                          <span className="text-white font-semibold">{r.other?.nom_prenom || "un membre"}</span>
                        </p>
                        <p className="text-cbm-muted text-xs mt-1">
                          {r.other?.fonction && `${r.other.fonction}`}
                          {r.other?.entreprise && ` · ${r.other.entreprise}`}
                        </p>
                      </div>
                    </div>

                    {/* Message */}
                    <div className="bg-navy-darker rounded p-3 mb-3">
                      <p className="text-cbm-label text-xs mb-1">Motif</p>
                      <p className="text-cbm-secondary text-sm leading-relaxed whitespace-pre-line">{r.message}</p>
                    </div>

                    {/* Date */}
                    <p className="text-cbm-muted text-xs mb-3 flex items-center gap-1">
                      <Clock size={12} />
                      {new Date(r.created_at).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" })}
                    </p>

                    {/* Actions */}
                    <div className="flex items-center gap-2 flex-wrap">
                      {r.other?.profile_id && (
                        <button
                          onClick={() => navigate(`/annuaire/${r.other.profile_id}`)}
                          className="cbm-btn-secondary text-xs px-3 py-2"
                          data-testid={`view-profile-${r._id}`}
                        >
                          Voir le profil
                        </button>
                      )}
                      <button
                        onClick={() => handleAskContactFromReco(r.other)}
                        disabled={!r.other?.profile_id}
                        className="cbm-btn-primary text-xs px-3 py-2 disabled:opacity-60"
                        data-testid={`ask-contact-${r._id}`}
                      >
                        <Send size={12} className="mr-1" /> Demander le contact
                      </button>
                      <button
                        onClick={() => handleIgnoreReco(r._id)}
                        disabled={processing === r._id}
                        className="text-xs px-3 py-2 rounded-lg font-medium transition-all inline-flex items-center gap-1.5"
                        style={{
                          background: "rgba(155,169,202,0.08)",
                          color: "#9BA9CA",
                          border: "1px solid rgba(155,169,202,0.2)",
                        }}
                        data-testid={`ignore-reco-${r._id}`}
                      >
                        <EyeOff size={12} /> {processing === r._id ? "..." : "Ignorer"}
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      </div>

      {contactTarget && (
        <ContactRequestModal
          profile={contactTarget}
          prefilledReason="Demande de mise en relation"
          onClose={() => setContactTarget(null)}
          onSuccess={() => { setContactTarget(null); loadData(); }}
        />
      )}
    </div>
  );
}
